import { useEffect, useState } from 'react';
import { getImageUrl } from '../../utils/imageUtils';
import { Link, useOutletContext, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Search,
  MapPin,
  ShieldCheck,
  Building2,
  Home,
  TreeDeciduous,
  Filter,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const propertyTypes = [
  { value: 'all', label: 'All Types' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'villa', label: 'Villa' },
  { value: 'land', label: 'Land' },
  { value: 'plot', label: 'Plot' },
  { value: 'project', label: 'Project' },
  { value: 'venture', label: 'Venture / Layout' }
];

const projectStatuses = [
  { value: 'all', label: 'All Statuses' },
  { value: 'ready', label: 'Ready to Move' },
  { value: 'under_construction', label: 'Under Construction' },
  { value: 'upcoming', label: 'Upcoming' }
];

const propertyTypeIcons = {
  apartment: Building2,
  villa: Home,
  land: TreeDeciduous,
  plot: TreeDeciduous,
  project: Building2,
  venture: Building2
};


export default function AgencyProperties() {
  const { agency } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    property_type: searchParams.get('type') || 'all',
    project_status: searchParams.get('status') || 'all',
    location_city: searchParams.get('city') || '',
    min_price: searchParams.get('min_price') || '',
    max_price: searchParams.get('max_price') || ''
  });

  useEffect(() => {
    fetchProperties();
  }, [agency.slug]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/public/properties/${agency.slug}?limit=50`;

      if (filters.property_type && filters.property_type !== 'all') {
        url += `&property_type=${filters.property_type}`;
      }
      if (filters.project_status && filters.project_status !== 'all') {
        url += `&project_status=${filters.project_status}`;
      }
      if (filters.location_city) {
        url += `&location_city=${filters.location_city}`;
      }
      if (filters.min_price) {
        url += `&min_price=${filters.min_price}`;
      }
      if (filters.max_price) {
        url += `&max_price=${filters.max_price}`;
      }

      const response = await axios.get(url);
      setProperties(response.data.properties);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const applyFilters = () => {
    fetchProperties();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      property_type: 'all',
      project_status: 'all',
      location_city: '',
      min_price: '',
      max_price: ''
    });
  };

  const formatPrice = (price) => {
    if (price >= 10000000) return `₹${(price / 10000000).toFixed(2)} Cr`;
    if (price >= 100000) return `₹${(price / 100000).toFixed(2)} L`;
    return `₹${price.toLocaleString()}`;
  };

  const hasActiveFilters = filters.property_type !== 'all' ||
    filters.project_status !== 'all' ||
    filters.location_city ||
    filters.min_price ||
    filters.max_price;

  return (
    <div className="min-h-screen bg-navy-50" data-testid="agency-properties">
      {/* Header */}
      <div className="bg-white border-b border-navy-100 py-8 px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-heading font-bold text-navy-900 mb-2">Our Properties</h1>
          <p className="text-navy-600">{total} properties available</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters Bar */}
        <div className="bg-white rounded-xl shadow-soft p-4 mb-8">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy-400" />
                <Input
                  placeholder="Search by city..."
                  value={filters.location_city}
                  onChange={(e) => handleFilterChange('location_city', e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>

            <Select
              value={filters.property_type}
              onValueChange={(v) => handleFilterChange('property_type', v)}
            >
              <SelectTrigger className="w-[160px]" data-testid="type-filter">
                <SelectValue placeholder="Property Type" />
              </SelectTrigger>
              <SelectContent>
                {propertyTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.project_status}
              onValueChange={(v) => handleFilterChange('project_status', v)}
            >
              <SelectTrigger className="w-[180px]" data-testid="status-filter">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {projectStatuses.map(status => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
              data-testid="more-filters-btn"
            >
              <Filter className="w-4 h-4" />
              More Filters
            </Button>

            <Button
              onClick={applyFilters}
              className="bg-navy-900 hover:bg-navy-800"
              data-testid="apply-filters-btn"
            >
              Apply
            </Button>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="text-navy-500"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Extended Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-navy-100 grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Min Price (₹)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 5000000"
                  value={filters.min_price}
                  onChange={(e) => handleFilterChange('min_price', e.target.value)}
                  data-testid="min-price-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Max Price (₹)</Label>
                <Input
                  type="number"
                  placeholder="e.g., 50000000"
                  value={filters.max_price}
                  onChange={(e) => handleFilterChange('max_price', e.target.value)}
                  data-testid="max-price-input"
                />
              </div>
            </div>
          )}
        </div>

        {/* Properties Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="animate-pulse">
                <div className="h-64 bg-navy-200 rounded-xl mb-4" />
                <div className="h-6 bg-navy-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-navy-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : properties.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-navy-300" />
            <h3 className="text-lg font-semibold text-navy-900 mb-2">No properties found</h3>
            <p className="text-navy-600 mb-4">Try adjusting your filters</p>
            {hasActiveFilters && (
              <Button variant="outline" onClick={clearFilters}>
                Clear Filters
              </Button>
            )}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {properties.map((property, index) => {
              const TypeIcon = propertyTypeIcons[property.property_type] || Building2;
              return (
                <motion.div
                  key={property.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                >
                  <Link to={`/agency/${agency.slug}/property/${property.id}`}>
                    <div className="group property-card bg-white rounded-xl overflow-hidden shadow-soft hover:shadow-float transition-shadow" data-testid={`property-card-${property.id}`}>
                      <div className="relative h-56">
                        {property.images?.[0] ? (
                          <img
                            src={getImageUrl(property.images[0])}
                            alt={property.title}
                            className="w-full h-full object-cover property-card-image"
                          />
                        ) : (
                          <div className="w-full h-full bg-navy-100 flex items-center justify-center">
                            <TypeIcon className="w-16 h-16 text-navy-300" />
                          </div>
                        )}
                        <Badge className="absolute top-4 left-4 bg-navy-900/80">
                          {property.property_type.charAt(0).toUpperCase() + property.property_type.slice(1)}
                        </Badge>
                        <Badge className="absolute top-4 right-4 bg-green-500 text-xs">
                          <ShieldCheck className="w-3 h-3 mr-1" />
                          Direct
                        </Badge>
                        {property.project_status !== 'ready' && (
                          <Badge className="absolute bottom-4 left-4 bg-orange-500">
                            {property.project_status === 'under_construction' ? 'Under Construction' : 'Upcoming'}
                          </Badge>
                        )}
                      </div>
                      <div className="p-5">
                        <h3 className="text-lg font-semibold text-navy-900 mb-2 group-hover:text-sky-500 transition-colors truncate">
                          {property.title}
                        </h3>
                        <p className="text-navy-500 flex items-center gap-1 mb-3 text-sm">
                          <MapPin className="w-4 h-4" />
                          {property.location_area}, {property.location_city}
                        </p>
                        <div className="flex items-center justify-between pt-3 border-t border-navy-100">
                          <span className="text-xl font-bold text-navy-900">
                            {property.property_type === 'venture' && property.price_min && property.price_max
                              ? `${formatPrice(property.price_min)} - ${formatPrice(property.price_max)}`
                              : formatPrice(property.price)
                            }
                          </span>
                          <span className="text-sm text-navy-500">
                            {property.size} {property.size_unit}
                          </span>
                        </div>
                        {property.property_type === 'venture' && property.units_total > 0 && (
                          <div className="mt-2 text-xs text-navy-500">
                            <span className="text-green-600 font-medium">{property.units_available || 0}</span>
                            <span>/{property.units_total} plots available</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
